import { JSX, useEffect, useRef, useState } from "react";

import { MapContainer, ZoomControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./MapStyles.css";
import ChartSkeleton from "src/components/ChartSkeleton/ChartSkeleton";
import { type IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import ChangeMapView from "src/components/Map/Controls/ChangeMapView/ChangeMapView";
import GeoMapLayer from "src/components/Map/MapLayers/GeoMapLayer";
import MeasureLayer from "src/components/Map/MapLayers/MeasureLayer/MeasureLayer";
import { calculateCenterAndBounds, type LeafletBoundsAndCenter } from "src/lib/leaflet.helper";
import { type MeasuresSummarizedDto } from "src/types/dashboardDatasetDto";
import type LocationType from "src/types/LocationType";

const styles = {
  mapContainer: { height: "100%", width: "100%" },
};

function CenterMap({
  selectedTab,
  measuresData,
  displayedType,
}: {
  selectedTab: IntervalTab;
  measuresData: MeasuresSummarizedDto;
  displayedType: LocationType;
}): JSX.Element | null {
  const [boundingBox, setBoundingBox] = useState<LeafletBoundsAndCenter>();

  const mapRef = useRef<L.Map | null>(null);

  const handleMapReady = (mapInstance: { target: L.Map }): void => {
    mapRef.current = mapInstance.target;
  };

  useEffect(() => {
    if (!boundingBox) {
      setBoundingBox(calculateCenterAndBounds(measuresData));
    }
  }, [boundingBox, measuresData]);

  if (!boundingBox) return <ChartSkeleton />;

  if (!measuresData.data) {
    return null;
  }

  return (
    <MapContainer
      ref={mapRef}
      style={styles.mapContainer}
      center={boundingBox.center}
      bounds={boundingBox.bounds}
      attributionControl={false}
      whenReady={() => handleMapReady}
      zoomControl={false}
    >
      <ChangeMapView bounds={boundingBox.bounds} />
      <GeoMapLayer />
      <MeasureLayer measuresData={measuresData} selectedTab={selectedTab} displayedType={displayedType} />
      <ZoomControl position='bottomright' />
    </MapContainer>
  );
}

export default CenterMap;
